import { Article, toArticle, UxEvaluationCount } from "../model/article";
import { CommonResponseDto } from "../../../shared/api/common-response.dto";
import { getApiClient } from "../../../shared/api/client.api";
import { AxiosError } from "axios";
import {
  type ArticleGetResDTO,
  ArticlePostReqDTO,
  type ArticlePostResDTO,
  ArticlePutReqDTO,
  type ArticlePutResDTO,
  type UxEvaluationCountGetResDTO,
} from "./article-dto";

export const manageArticleApi = (() => {
  const apiClient = getApiClient();

  const getArticleList = async (): Promise<Article[]> => {
    try {
      const articleList =
        await apiClient.get<
          CommonResponseDto<{ articles: ArticleGetResDTO[] }>
        >("/articles");

      if (
        !articleList.data.success ||
        !articleList.data.data ||
        !articleList.data.data.articles
      ) {
        throw new Error("ArticleList Data Not Found");
      }

      return articleList.data.data.articles.map((article: ArticleGetResDTO) => {
        return toArticle(article);
      });
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        throw e;
      }

      throw e;
    }
  };

  const getArticleListPaginated = async (
    page = 1,
    limit = 10,
    filters?: string,
    sortBy?: string,
    sortOrder?: string,
  ): Promise<{ articles: Article[]; hasMore: boolean; total: number }> => {
    try {
      // 필터와 정렬 파라미터가 있으면 URL에 추가
      const filterParams = filters ? `&${filters}` : "";
      const sortByParams = sortBy ? `&sortBy=${sortBy}` : "";
      const sortOrderParams = sortOrder ? `&sortOrder=${sortOrder}` : "";
      const response = await apiClient.get<
        CommonResponseDto<{
          articles: ArticleGetResDTO[];
          hasMore: boolean;
          total: number;
        }>
      >(
        `/articles?page=${page}&limit=${limit}${filterParams}${sortByParams}${sortOrderParams}`,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error("ArticleList Data Not Found");
      }

      return {
        articles: response.data.data.articles.map(
          (article: ArticleGetResDTO) => {
            return toArticle(article);
          },
        ),
        hasMore: response.data.data.hasMore,
        total: response.data.data.total,
      };
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        throw e;
      }

      throw e;
    }
  };

  const getArticle = async (articleId: string): Promise<Article> => {
    try {
      const article = await apiClient.get<CommonResponseDto<ArticleGetResDTO>>(
        `/articles/${articleId}`,
      );

      if (!article.data.success || !article.data.data) {
        throw new Error("Article Data Not Found");
      }

      return toArticle(article.data.data);
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        throw e;
      }

      throw e;
    }
  };

  const addArticle = async (article: ArticlePostReqDTO) => {
    try {
      const response = await apiClient.post<
        CommonResponseDto<ArticlePostResDTO>
      >("/articles", article);

      if (!response.data.success || !response.data.data) {
        throw new Error("Failed to add article");
      }

      return response.data.data;
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        throw e;
      } else if (e instanceof Error) {
        throw new Error(e.message || "Unknown error occurred");
      } else {
        throw new Error("An unknown error occurred");
      }
    }
  };

  const updateArticle = async (
    articleId: string,
    article: ArticlePutReqDTO,
  ) => {
    try {
      const response = await apiClient.put<CommonResponseDto<ArticlePutResDTO>>(
        `/articles/${articleId}`,
        article,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error("Failed to update article");
      }

      return response.data.data;
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        throw e;
      }
    }
  };

  const deleteArticle = async (articleId: string) => {
    try {
      await apiClient.delete(`/articles/${articleId}`);
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        throw e;
      }
    }
  };

  const getUxEvaluationCount = async (): Promise<UxEvaluationCount> => {
    try {
      const response = await apiClient.get<
        CommonResponseDto<UxEvaluationCountGetResDTO>
      >("/articles/uxEvaluationCount");

      if (!response.data.success || !response.data.data) {
        throw new Error("UxEvaluationCount Data Not Found");
      }

      return {
        goodUxCount: response.data.data.goodUxCount,
        badUxCount: response.data.data.badUxCount,
      };
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        throw e;
      }

      throw e;
    }
  };

  return {
    getArticleList,
    getArticleListPaginated,
    getArticle,
    addArticle,
    updateArticle,
    deleteArticle,
    getUxEvaluationCount,
  };
})();
